import { completedJobCoveringWindow } from '../utils/computeJobCoverage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  cancelTruthComputeJob,
  fetchTruthComputeJobs,
  fetchTruthStatus,
  truthLastComputedMeta,
  triggerTruthCompute,
} from '../services/profitabilityTruthClient';
import type {
  TruthComputeJobRecord,
  TruthComputeScope,
  TruthStatusResponse,
} from '../types/profitabilityTruth';

export interface UseTruthProfitabilityPanelOptions {
  startDate: string;
  endDate: string;
  /** Called when a compute job completes so merged-costs can reload. */
  onDataRefresh?: () => void;
  /** Fetch coverage summary via status (can be slower on wide ranges). */
  includeCoverage?: boolean;
  /**
   * Athena/Iceberg source probes for automatic status refresh (default false).
   * Manifest-only status is enough for last_computed / UI; probes can take minutes on wide ranges.
   * Use `checkSources()` for an explicit on-demand probe.
   */
  probeSources?: boolean;
}

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError') ||
    (err instanceof Error && err.name === 'AbortError')
  );
}

export function useTruthProfitabilityPanel({
  startDate,
  endDate,
  onDataRefresh,
  includeCoverage = false,
  probeSources = false,
}: UseTruthProfitabilityPanelOptions) {
  const [statusData, setStatusData] = useState<TruthStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [sourcesChecking, setSourcesChecking] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);

  const [computeJobs, setComputeJobs] = useState<TruthComputeJobRecord[]>([]);
  const [computeJobsLoading, setComputeJobsLoading] = useState(false);
  const [computeTriggerLoading, setComputeTriggerLoading] = useState(false);
  const [computeTriggerError, setComputeTriggerError] = useState<string | null>(null);
  const [cancellingJobIds, setCancellingJobIds] = useState<Set<string>>(new Set());
  const [recomputeWarning, setRecomputeWarning] = useState(false);
  const [pendingComputeScope, setPendingComputeScope] = useState<TruthComputeScope>('all');
  const [showJobsPanel, setShowJobsPanel] = useState(false);

  const jobsPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevJobsRef = useRef<TruthComputeJobRecord[]>([]);
  /** Monotonic id so slower in-flight status responses cannot overwrite a newer range. */
  const statusRequestGenRef = useRef(0);
  const statusAbortRef = useRef<AbortController | null>(null);

  const loadStatus = useCallback(
    async (opts: { probe: boolean; clearExisting: boolean; forSourcesCheck?: boolean }) => {
      statusAbortRef.current?.abort();
      const ac = new AbortController();
      statusAbortRef.current = ac;
      const requestId = ++statusRequestGenRef.current;
      const forStart = startDate;
      const forEnd = endDate;

      if (opts.forSourcesCheck) {
        setSourcesChecking(true);
      } else {
        setStatusLoading(true);
      }
      setStatusError(null);
      if (opts.clearExisting) {
        // Drop previous range immediately so amber warnings never show a stale window.
        setStatusData(null);
      }

      try {
        const status = await fetchTruthStatus(forStart, forEnd, {
          includeCoverage,
          probeSources: opts.probe,
          signal: ac.signal,
        });
        if (requestId !== statusRequestGenRef.current) return;
        // Defense: ignore responses that don't match the requested window.
        if (status.date_range?.start !== forStart || status.date_range?.end !== forEnd) {
          return;
        }
        setStatusData(status);
      } catch (err) {
        if (requestId !== statusRequestGenRef.current || isAbortError(err) || ac.signal.aborted) {
          return;
        }
        setStatusError(err instanceof Error ? err.message : 'Failed to load pipeline status');
        if (opts.clearExisting) {
          setStatusData(null);
        }
      } finally {
        if (requestId === statusRequestGenRef.current) {
          if (opts.forSourcesCheck) {
            setSourcesChecking(false);
          } else {
            setStatusLoading(false);
          }
        }
      }
    },
    [startDate, endDate, includeCoverage],
  );

  const refreshStatus = useCallback(async () => {
    await loadStatus({ probe: probeSources, clearExisting: true });
  }, [loadStatus, probeSources]);

  /** On-demand Athena COUNT probes for green/red source dots (can be slow on wide ranges). */
  const checkSources = useCallback(async () => {
    await loadStatus({ probe: true, clearExisting: false, forSourcesCheck: true });
  }, [loadStatus]);

  const refreshJobs = useCallback(async () => {
    try {
      const jobs = await fetchTruthComputeJobs();
      setComputeJobs(jobs);
      return jobs;
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    return () => {
      statusAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    setRecomputeWarning(false);
    setPendingComputeScope('all');
  }, [startDate, endDate]);

  useEffect(() => {
    void refreshJobs();
  }, [refreshJobs]);

  useEffect(() => {
    const hasActive = computeJobs.some((j) => j.status === 'pending' || j.status === 'running');

    const prevActiveIds = new Set(
      prevJobsRef.current
        .filter((j) => j.status === 'running' || j.status === 'pending')
        .map((j) => j.jobId),
    );
    const justCompleted = computeJobs.filter(
      (j) => j.status === 'completed' && prevActiveIds.has(j.jobId),
    );
    if (justCompleted.length > 0) {
      onDataRefresh?.();
      void refreshStatus();
    }
    prevJobsRef.current = computeJobs;

    if (hasActive && !jobsPollRef.current) {
      jobsPollRef.current = setInterval(() => {
        void refreshJobs();
      }, 3000);
    } else if (!hasActive && jobsPollRef.current) {
      clearInterval(jobsPollRef.current);
      jobsPollRef.current = null;
    }

    return () => {
      if (jobsPollRef.current) {
        clearInterval(jobsPollRef.current);
        jobsPollRef.current = null;
      }
    };
  }, [computeJobs, refreshJobs, onDataRefresh, refreshStatus]);

  const doCompute = useCallback(async (scope: TruthComputeScope = 'all') => {
    setRecomputeWarning(false);
    setPendingComputeScope(scope);
    setComputeTriggerLoading(true);
    setComputeTriggerError(null);
    try {
      await triggerTruthCompute(startDate, endDate, scope);
      await refreshJobs();
      setShowJobsPanel(true);
    } catch (err) {
      setComputeTriggerError(err instanceof Error ? err.message : 'Computation failed');
    } finally {
      setComputeTriggerLoading(false);
    }
  }, [startDate, endDate, refreshJobs]);

  const conflictingCompletedJob = useMemo(
    () => completedJobCoveringWindow(computeJobs, startDate, endDate, pendingComputeScope),
    [computeJobs, startDate, endDate, pendingComputeScope],
  );

  const handleCompute = useCallback((scope: TruthComputeScope = 'all') => {
    setPendingComputeScope(scope);
    // Every day already computed (by day jobs or an older window job).
    const conflict = completedJobCoveringWindow(computeJobs, startDate, endDate, scope);
    if (conflict) {
      setRecomputeWarning(true);
      return;
    }
    void doCompute(scope);
  }, [computeJobs, startDate, endDate, doCompute]);

  const cancelJob = useCallback(
    async (jobId: string) => {
      setCancellingJobIds((prev) => new Set(prev).add(jobId));
      setComputeTriggerError(null);
      try {
        await cancelTruthComputeJob(jobId);
        await refreshJobs();
      } catch (err) {
        setComputeTriggerError(err instanceof Error ? err.message : 'Failed to cancel job');
      } finally {
        setCancellingJobIds((prev) => {
          const next = new Set(prev);
          next.delete(jobId);
          return next;
        });
      }
    },
    [refreshJobs],
  );

  const hasActiveJob = computeJobs.some((j) => j.status === 'pending' || j.status === 'running');

  /** Never surface status for a different Report period than the pickers. */
  const statusDataForRange = useMemo(() => {
    if (!statusData) return null;
    if (statusData.date_range.start !== startDate || statusData.date_range.end !== endDate) {
      return null;
    }
    return statusData;
  }, [statusData, startDate, endDate]);

  const lastComputed = useMemo(
    () => truthLastComputedMeta(statusDataForRange?.last_computed ?? null),
    [statusDataForRange],
  );

  const sourcesProbed = useMemo(
    () =>
      Boolean(
        statusDataForRange?.sources.some(
          (s) => s.status === 'available' || s.status === 'unavailable',
        ),
      ),
    [statusDataForRange],
  );

  return {
    statusData: statusDataForRange,
    statusLoading,
    sourcesChecking,
    sourcesProbed,
    statusError,
    /** ISO window currently requested for status / compute (matches Report period pickers). */
    statusWindow: { startDate, endDate },
    lastComputed,
    computeJobs,
    computeJobsLoading,
    computeTriggerLoading,
    computeTriggerError,
    recomputeWarning,
    pendingComputeScope,
    showJobsPanel,
    setShowJobsPanel,
    hasActiveJob,
    conflictingCompletedJob,
    handleCompute,
    doCompute,
    cancelJob,
    cancellingJobIds,
    setRecomputeWarning,
    refreshStatus,
    checkSources,
    refreshJobs: async () => {
      setComputeJobsLoading(true);
      try {
        return await refreshJobs();
      } finally {
        setComputeJobsLoading(false);
      }
    },
  };
}
